import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LOGO_URL, NAV_LINKS } from "@/lib/data";
import { openCalendly } from "@/lib/calendly";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (open) {
      const original = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = original;
      };
    }
  }, [open]);

  const isActive = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-black/65 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-10 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-3 group">
          <div className="h-10 w-10 rounded-xl overflow-hidden ring-2 ring-[hsl(var(--accent))]/40 group-hover:ring-[hsl(var(--accent))] transition-all duration-300">
            <img src={LOGO_URL} alt="LD Media logo" className="h-full w-full object-cover" />
          </div>
          <span className="font-display text-lg font-bold tracking-tight">
            LD <span className="text-[hsl(var(--accent))]">Media</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Hovedmenu">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.replace(/\//g, "")}`}
              className={`link-underline text-sm transition-colors ${
                isActive(l.to) ? "text-[hsl(var(--accent))]" : "text-white/80 hover:text-white"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={openCalendly}
            data-testid="header-cta-btn"
            className="btn-pop hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[hsl(var(--accent))] text-[hsl(var(--accent-ink))] text-sm font-semibold hover:shadow-[0_8px_30px_-6px_hsla(75,100%,67%,0.6)]"
          >
            Book et møde
            <span aria-hidden>→</span>
          </button>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden h-10 w-10 grid place-items-center rounded-md border border-white/10"
            aria-label="Menu"
            aria-expanded={open}
          >
            <div className="space-y-1.5">
              <span className={`block h-0.5 w-5 bg-white transition ${open ? "translate-y-2 rotate-45" : ""}`} />
              <span className={`block h-0.5 w-5 bg-white transition ${open ? "opacity-0" : ""}`} />
              <span className={`block h-0.5 w-5 bg-white transition ${open ? "-translate-y-2 -rotate-45" : ""}`} />
            </div>
          </button>
        </div>
      </div>

      {/* Backdrop overlay */}
      <div
        onClick={() => setOpen(false)}
        className={`lg:hidden fixed inset-0 top-[72px] z-[60] bg-black/80 backdrop-blur-sm transition-opacity duration-300 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden="true"
      />

      {/* Slide-in drawer */}
      <aside
        data-testid="mobile-menu"
        className={`lg:hidden fixed top-[72px] right-0 bottom-0 z-[70] w-[78%] max-w-[340px] border-l border-white/10 shadow-2xl transition-transform duration-300 ease-out ${
          open ? "translate-x-0 pointer-events-auto" : "translate-x-full pointer-events-none"
        }`}
        style={{
          backgroundColor: "#0a0a0a",
          visibility: open ? "visible" : "hidden",
          transitionProperty: open ? "transform" : "transform, visibility",
          transitionDelay: open ? "0ms" : "300ms",
        }}
        aria-hidden={!open}
      >
        <div className="h-full overflow-y-auto px-6 py-7 flex flex-col gap-1">
          <div className="text-[10px] uppercase tracking-[0.28em] text-white/40 mb-5">Menu</div>
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`group flex items-center justify-between py-3 border-b border-white/5 hover:text-[hsl(var(--accent))] transition-colors duration-200 ${
                isActive(l.to) ? "text-[hsl(var(--accent))]" : "text-white/85"
              }`}
              data-testid={`mobile-nav-${l.to.replace(/\//g, "")}`}
            >
              <span className="text-base font-medium">{l.label}</span>
              <span className="text-white/30 group-hover:text-[hsl(var(--accent))] group-hover:translate-x-1 transition-all">
                →
              </span>
            </Link>
          ))}
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              openCalendly();
            }}
            data-testid="mobile-cta-btn"
            className="mt-6 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[hsl(var(--accent))] text-[hsl(var(--accent-ink))] font-semibold"
          >
            Book et møde →
          </button>

          <div className="mt-auto pt-8 text-xs text-white/40 space-y-1.5">
            <div>ldmediaa1@gmail.com</div>
            <div>+45 91 64 92 29</div>
          </div>
        </div>
      </aside>
    </header>
  );
};
